<template>
  <div class="settings-panel">
    <!-- 头部 -->
    <div class="settings-header">
      <span class="settings-title">⚙️ 系统设置</span>
      <var-icon name="close" class="close-icon" @click="$emit('close')" />
    </div>

    <!-- 主体 -->
    <div class="settings-body">
      <!-- 左侧菜单 -->
      <div class="settings-sidebar">
        <div v-for="item in menuItems" :key="item.key" class="menu-item" :class="{ active: activeTab === item.key }"
          @click="switchTab(item.key)">
          <span class="menu-icon">{{ item.icon }}</span>
          <span class="menu-label">{{ item.label }}</span>
        </div>
      </div>

      <!-- 右侧内容 -->
      <div class="settings-content">
        <AccountInfo v-if="activeTab === 'account'" ref="accountInfoRef" />
        <PasswordSettings v-if="activeTab === 'password'" ref="passwordSettingsRef" />
        <UserManagement v-if="activeTab === 'users'" ref="userManagementRef" />
        <PositionManagement v-if="activeTab === 'positions'" ref="positionManagementRef" />
        <SalesPlatformManagement v-if="activeTab === 'platforms'" ref="platformManagementRef" />
        <BackupSettings v-if="activeTab === 'backup'" ref="backupSettingsRef" />
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="settings-footer">
      <var-button v-if="actionButton" :type="actionButton.type || 'primary'" @click="actionButton.action">
        {{ actionButton.text }}
      </var-button>
      <var-button plain @click="$emit('close')">关闭</var-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAppStore } from '@/stores'
import { useFeature } from '@/composables/useFeature'
import AccountInfo from './AccountInfo.vue'
import PasswordSettings from './PasswordSettings.vue'
import UserManagement from './UserManagement.vue'
import PositionManagement from './PositionManagement.vue'
import SalesPlatformManagement from './SalesPlatformManagement.vue'
import BackupSettings from './BackupSettings.vue'

const emit = defineEmits(['close'])

// ============================================================
// ✅ 使用新的权限命名规范
// ============================================================
const { can } = useFeature('system')

// ===== Refs =====
const activeTab = ref('account')
const accountInfoRef = ref(null)
const passwordSettingsRef = ref(null)
const userManagementRef = ref(null)
const positionManagementRef = ref(null)
const platformManagementRef = ref(null)
const backupSettingsRef = ref(null)

// ============================================================
// ✅ 菜单项（由权限控制）- 使用新命名规范
// ============================================================
const menuItems = computed(() => {
  const items = []

  // 账户信息 + 密码修改：需要 system_modify_password 权限
  if (can('system_modify_password')) {
    items.push({ key: 'account', icon: '👤', label: '账户信息' })
    items.push({ key: 'password', icon: '🔑', label: '密码修改' })
  }

  // 组织管理：需要 system_user_manage 权限
  if (can('system_user_manage')) {
    items.push({ key: 'users', icon: '👥', label: '组织管理' })
    items.push({ key: 'positions', icon: '🏷️', label: '职位管理' })
  }

  // 销售平台管理：需要 system_manage_platform 权限
  if (can('system_manage_platform')) {
    items.push({ key: 'platforms', icon: '🛒', label: '销售平台' })
  }

  // 数据备份：需要 system_create_backup 或 system_restore_backup 权限
  if (can('system_create_backup') || can('system_restore_backup')) {
    items.push({ key: 'backup', icon: '💾', label: '数据备份' })
  }

  return items
})

// ===== 底部按钮 =====
const actionButton = computed(() => {
  const map = {
    account: {
      text: '保存设置',
      type: 'primary',
      action: () => accountInfoRef.value?.saveCompany(),
    },
    password: {
      text: '修改密码',
      type: 'primary',
      action: () => passwordSettingsRef.value?.savePassword(),
    },
    users: {
      text: '+ 添加用户',
      type: 'primary',
      action: () => userManagementRef.value?.openUserForm(),
    },
    positions: {
      text: '+ 新增职位',
      type: 'primary',
      action: () => positionManagementRef.value?.openPositionForm(),
    },
    platforms: {
      text: '+ 新增平台',
      type: 'primary',
      action: () => platformManagementRef.value?.openForm(),
    },
    backup: {
      text: '创建备份',
      type: 'primary',
      action: () => backupSettingsRef.value?.createBackup(),
    },
  }
  return map[activeTab.value] || null
})

// ===== 切换 Tab =====
function switchTab(key) {
  activeTab.value = key
}

// ===== 切换 Tab 时刷新数据 =====
watch(activeTab, (newTab) => {
  if (newTab === 'users') {
    userManagementRef.value?.loadUsers?.()
    userManagementRef.value?.loadPositions?.()
  }
  if (newTab === 'positions') {
    positionManagementRef.value?.loadPositions?.()
  }
  if (newTab === 'platforms') {
    platformManagementRef.value?.loadPlatforms?.()
  }
  if (newTab === 'backup') {
    backupSettingsRef.value?.loadBackups?.()
  }
})

// ===== 初始化 =====
onMounted(() => {
  if (menuItems.value.length > 0) {
    activeTab.value = menuItems.value[0].key
  }
})
</script>

<style scoped>
.settings-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 780px;
  max-width: 100%;
  background: #fff;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--var-border-color, #e4e7ed);
  flex-shrink: 0;
}

.settings-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--var-text-primary);
}

.close-icon {
  font-size: 22px;
  cursor: pointer;
  color: var(--var-text-secondary);
  transition: color 0.2s;
}

.close-icon:hover {
  color: var(--var-danger-color, #f56c6c);
}

.settings-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.settings-sidebar {
  width: 160px;
  min-width: 140px;
  border-right: 1px solid var(--var-border-color, #e4e7ed);
  padding: 8px 0;
  overflow-y: auto;
  flex-shrink: 0;
  background: #fafafa;
}

.settings-sidebar::-webkit-scrollbar {
  width: 4px;
}

.settings-sidebar::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 4px;
}

.settings-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 13px;
  color: var(--var-text-secondary);
  border-radius: 4px;
  margin: 2px 8px;
  transition: all 0.2s;
}

.menu-item:hover {
  background: var(--var-hover-bg, #ecf5ff);
  color: var(--var-primary-color, #409eff);
}

.menu-item.active {
  background: var(--var-primary-color, #409eff);
  color: #fff;
}

.menu-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.menu-label {
  font-weight: 500;
}

.settings-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fff;
}

.settings-content::-webkit-scrollbar {
  width: 4px;
}

.settings-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 4px;
}

.settings-content::-webkit-scrollbar-track {
  background: transparent;
}

.settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid var(--var-border-color, #e4e7ed);
  flex-shrink: 0;
  background: #fafafa;
}

@media (max-width: 820px) {
  .settings-panel {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .settings-header {
    padding: 12px 16px;
  }

  .settings-title {
    font-size: 16px;
  }

  .settings-body {
    flex-direction: column;
  }

  .settings-sidebar {
    width: 100%;
    min-width: unset;
    border-right: none;
    border-bottom: 1px solid var(--var-border-color, #e4e7ed);
    display: flex;
    overflow-x: auto;
    padding: 4px 0;
    background: #fff;
    flex-shrink: 0;
  }

  .menu-item {
    white-space: nowrap;
    margin: 2px 4px;
    padding: 6px 12px;
    font-size: 12px;
    flex-shrink: 0;
  }

  .menu-item.active {
    background: var(--var-primary-color, #409eff);
    color: #fff;
  }

  .settings-content {
    padding: 12px 14px;
  }

  .settings-footer {
    padding: 10px 14px;
  }

  .settings-footer .var-button {
    font-size: 12px;
    height: 30px;
  }
}

@media (max-width: 480px) {
  .settings-header {
    padding: 10px 12px;
  }

  .settings-title {
    font-size: 14px;
  }

  .menu-item {
    font-size: 11px;
    padding: 4px 10px;
  }

  .settings-content {
    padding: 10px 12px;
  }
}
</style>